<template>
  <div class="container">
    <header class="text-center wizard-header-text">
      <h1>Assessment Wizard</h1>
      <p class="gb-text-2">Find the most relevant assessments for your students</p>
    </header>

    <WizardOverlayContainer>
      <form @submit.prevent="handleSubmit" class="wizard-form">
        <!-- Filters Section -->
        <div class="form-section">
          <p class="section-heading">Filters (optional)</p>
          <div class="filters-vertical">
            <!-- Subject -->
            <div class="filter-group">
              <label for="subject-filter-btn" class="filter-label">Subject</label>
              <a11y-dropdown
                :dropdown-props="{ tag: 'div' }"
                :button-props="{ id: 'subject-filter-btn', 'data-role': 'trigger', class: 'dropdown-toggle btn btn-default' }"
                :dom-attributes="{ role: 'menu', id: 'subject-dropdown' }">
                <template slot="button-contents">
                  <span :class="{ 'text-muted': !formData.filters.subject }">
                    {{ subjectSelectorText }}
                    <span class="caret" />
                  </span>
                </template>
                <template slot="dropdown-items">
                  <li class="dropdown-item" role="menuitem">
                    <a role="button" @click="updateFilters({ field: 'subject', value: '' })">
                      All Subjects
                    </a>
                  </li>
                  <li
                    v-for="subject in subjects"
                    :key="subject.subject"
                    class="dropdown-item"
                    role="menuitem">
                    <a
                      role="button"
                      @click="updateFilters({ field: 'subject', value: subject.subject })">
                      {{ subject.subject }}
                    </a>
                  </li>
                </template>
              </a11y-dropdown>
            </div>

            <!-- Category -->
            <div class="filter-group">
              <label for="category-filter-btn" class="filter-label">Category</label>
              <a11y-dropdown
                :dropdown-props="{ tag: 'div' }"
                :button-props="{ id: 'category-filter-btn', 'data-role': 'trigger', class: 'dropdown-toggle btn btn-default' }"
                :dom-attributes="{ role: 'menu', id: 'category-dropdown' }">
                <template slot="button-contents">
                  <span :class="{ 'text-muted': !formData.filters.category }">
                    {{ categorySelectorText }}
                    <span class="caret" />
                  </span>
                </template>
                <template slot="dropdown-items">
                  <li class="dropdown-item" role="menuitem">
                    <a role="button" @click="updateFilters({ field: 'category', value: '' })">
                      All Categories
                    </a>
                  </li>
                  <li
                    v-for="category in categories"
                    :key="category"
                    class="dropdown-item"
                    role="menuitem">
                    <a
                      role="button"
                      @click="updateFilters({ field: 'category', value: category })">
                      {{ category }}
                    </a>
                  </li>
                </template>
              </a11y-dropdown>
            </div>

            <!-- Grade -->
            <div class="filter-group">
              <label for="grade-filter-btn" class="filter-label">Grade</label>
              <a11y-dropdown
                :dropdown-props="{ tag: 'div' }"
                :button-props="{ id: 'grade-filter-btn', 'data-role': 'trigger', class: 'dropdown-toggle btn btn-default' }"
                :dom-attributes="{ role: 'menu', id: 'grade-dropdown' }">
                <template slot="button-contents">
                  <span :class="{ 'text-muted': !formData.filters.gradeLevel }">
                    {{ gradeSelectorText }}
                    <span class="caret" />
                  </span>
                </template>
                <template slot="dropdown-items">
                  <li class="dropdown-item" role="menuitem">
                    <a role="button" @click="updateFilters({ field: 'gradeLevel', value: '' })">
                      All Grades
                    </a>
                  </li>
                  <li
                    v-for="grade in grades"
                    :key="grade"
                    class="dropdown-item"
                    role="menuitem">
                    <a
                      role="button"
                      @click="updateFilters({ field: 'gradeLevel', value: grade })">
                      {{ grade }}
                    </a>
                  </li>
                </template>
              </a11y-dropdown>
            </div>
          </div>
        </div>

        <!-- Natural Language Query -->
        <div class="form-section">
          <label for="assessment-query" class="section-heading">What are you looking for?</label>
          <textarea
            id="assessment-query"
            v-model="formData.query"
            rows="3"
            class="query-textarea"
            placeholder="Describe the assessments you need. For example: 'Reading comprehension assessments for students with dyslexia'"
          />
        </div>

        <!-- File Upload -->
        <div class="form-section">
          <label for="assessment-files" class="section-heading">Upload files (optional)</label>
          <div class="file-upload-area">
            <input
              id="assessment-files"
              type="file"
              multiple
              @change="handleFileUpload"
              class="file-input"
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
            >
            <div v-if="formData.files.length > 0" class="uploaded-files">
              <p class="uploaded-files-label">Uploaded files:</p>
              <ul class="file-list">
                <li
                  v-for="(file, index) in formData.files"
                  :key="index"
                  class="file-item">
                  {{ file.name }}
                  <button
                    type="button"
                    @click="removeFile(index)"
                    class="remove-file-btn">
                    ×
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="!isFormValid || loading">
            Search Assessments
            <i v-if="loading" class="fa fa-circle-o-notch fa-spin" />
          </button>
        </div>
      </form>
    </WizardOverlayContainer>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import A11yDropdown from '@/components/A11yDropdown.vue'
import WizardOverlayContainer from '@/components/WizardOverlayContainer.vue'

export default {
  name: 'AssessmentWizard',
  components: {
    WizardOverlayContainer,
    A11yDropdown,
  },
  data() {
    return {
      formData: {
        query: '',
        files: [],
        filters: {
          subject: '',
          gradeLevel: '',
          category: '',
        },
      },
      selectedSubject: null,
    }
  },
  computed: {
    ...mapGetters({
      subjects: 'contentProfile/publishedSubjects',
      loading: 'agenticSearch/loading',
      hasResults: 'agenticSearch/hasAssessmentResults'
    }),
    subjectSelectorText() {
      return this.selectedSubject ? this.selectedSubject.subject : 'Select Subject'
    },
    categories() {
      return (this.selectedSubject && this.selectedSubject.categories) || []
    },
    grades() {
      return (this.selectedSubject && this.selectedSubject.grades) || []
    },
    gradeSelectorText() {
      if (!this.formData.filters.gradeLevel) {
        return 'Select Grade'
      }
      const grade = this.grades.find(g => g === this.formData.filters.gradeLevel)
      return grade ? grade : 'Select Grade'
    },
    categorySelectorText() {
      if (!this.formData.filters.category) {
        return 'Select Category'
      }
      const category = this.categories.find(c => c === this.formData.filters.category)
      return category ? category : 'Select Category'
    },
    isFormValid() {
      const hasQuery = this.formData.query.trim().length > 0
      const hasDropdownSelection = this.formData.filters.subject || this.formData.filters.category || this.formData.filters.gradeLevel
      const hasFiles = this.formData.files.length > 0
      return hasQuery || hasDropdownSelection || hasFiles
    },
  },
  methods: {
    ...mapActions({
      searchAssessments: 'agenticSearch/searchAssessments',
    }),
    updateFilters({ field, value }) {
      if (field === 'subject') {
        this.selectedSubject = this.subjects.find(s => s.subject === value) || null
        this.formData.filters.subject = value
        this.formData.filters.category = ''
        this.formData.filters.gradeLevel = ''
      } else {
        this.formData.filters[field] = value
      }
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files)
      this.formData.files = [...this.formData.files, ...files]
      event.target.value = ''
    },
    removeFile(index) {
      this.formData.files.splice(index, 1)
    },
    async handleSubmit() {
      try {
        await this.searchAssessments({
          query: this.formData.query,
          files: this.formData.files,
          filters: this.formData.filters,
        })
      } catch (error) {
        console.error('Error searching assessments:', error)
      }
    },
  },
  watch: {
    hasResults(hasResults) {
      if (hasResults) {
        this.$router.push('/browse/assessments?ref=wizard')
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '@/variables.less';
.filter-group {
  margin-bottom: 1rem;
}
.form-section {
  margin-bottom: 1rem;
}
textarea {
  width: 100%;
}
</style>
